import { Lock } from '@mui/icons-material';
import {
  type PlayerProfileResponse,
  type PublicMeasurementAveragesResponseRecordMeasurementComparisonRelatedResponseRelatedPagedData,
  type PublicMeasurementResponseRecord,
} from '@on3/api';
import { Measurement } from 'components/Athlete/Vitals/Measurement';
import { Link } from 'components/Link';
import { usePlayerProfile } from 'contexts/PlayerProfile/PlayerProvider';
import { useMemo } from 'react';
import { createTitle } from 'utils/meta';

import styles from './AthleteVitals.module.scss';

interface VitalsProps {
  player: PlayerProfileResponse;
  averages?: PublicMeasurementAveragesResponseRecordMeasurementComparisonRelatedResponseRelatedPagedData | null;
  measurements?: PublicMeasurementResponseRecord[] | null;
}

export const Vitals = ({
  player,
  measurements = [],
  averages = {},
}: VitalsProps) => {
  const { state: { adminPlayer } = {} } = usePlayerProfile();

  const sportOrg = useMemo(() => {
    if (player.defaultSport?.key === 1) return 'NFL Combine';
    if (player.defaultSport?.key === 2) return 'NBA Combine';

    return null;
  }, [player.defaultSport]);

  const numYears = averages?.relatedModel?.numberOfYears ?? 5;

  measurements = (
    measurements?.length
      ? measurements
      : ([
          {
            key: 0,
            measurementTypeKey: 1,
            type: 'Height',
          },
          {
            key: 1,
            measurementTypeKey: 2,
            type: 'Weight',
          },
          {
            key: 2,
            measurementTypeKey: 6,
            type: '40-Yard Dash',
          },
        ] as PublicMeasurementResponseRecord[])
  )
    .sort((a, b) => b.key - a.key)
    ?.filter(
      (m, i, arr) =>
        arr.findIndex((v) => v.measurementTypeKey === m.measurementTypeKey) ===
        i,
    )
    ?.slice(0, 6);

  return (
    <figure aria-labelledby="vitals-legend" className={styles.block}>
      {adminPlayer && (
        <div className={styles.note}>
          <p>Add and update your height, weight, 40, bench press, and more.</p>
        </div>
      )}
      {adminPlayer && !adminPlayer?.latestPlayerPso && (
        <div className={styles.needSport}>
          <Lock />
          <p>
            Interested in updating your vitals? Please{' '}
            <Link
              href={`/rivals/${player.slug}/elite/network/profile/details/`}
            >
              add a sport
            </Link>{' '}
            to your profile first!
          </p>
        </div>
      )}

      <figcaption className={styles.legend} id="vitals-legend">
        <ul>
          <li>
            <strong>{player.name}</strong>
            <Link href={`/high-school/${player.highSchool?.urlSlug}`}>
              {player.highSchoolName}
            </Link>
          </li>
          <li>
            <strong>
              {createTitle('Top 300', player.positionAbbreviation, 'Avg')}
            </strong>
            <span>{`Avg. Last ${numYears} Years`}</span>
          </li>
          <li>
            <strong>{createTitle(sportOrg, 'Drafted')}</strong>
            <span>{`Avg. Last ${numYears} Years`}</span>
          </li>
        </ul>
      </figcaption>

      <div className={styles.graphs}>
        {measurements.map((measurement) => (
          <Measurement data={measurement} key={measurement.key} />
        ))}
      </div>
    </figure>
  );
};
